<template>
    <div class="fixed bottom-7 right-7 z-[400]">
        <!-- Launcher: white-blur circle with the mascot peeking out of the top -->
        <div v-if="!open" class="relative">
            <!-- Greeting bubble: the mascot waves and says hi, like logout-success -->
            <transition name="greet">
                <div
                    v-if="showGreeting"
                    class="absolute bottom-full right-0 mb-3 w-max max-w-[220px] rounded-2xl rounded-br-sm border border-white/60 bg-white/80 px-3.5 py-2 text-sm font-medium text-gray-700 shadow-lg backdrop-blur-md"
                >
                    {{ greeting }}
                </div>
            </transition>

            <button
                type="button"
                class="launcher-btn group relative flex h-[72px] w-[72px] cursor-pointer items-center justify-center rounded-full border border-white/60 bg-white/40 shadow-xl backdrop-blur-md transition-all duration-200 hover:scale-105 hover:bg-white/60 hover:shadow-2xl active:scale-95"
                :aria-label="t('vue.ai_widget_open')"
                @click="openWidget"
            >
                <span class="launcher-pulse" aria-hidden="true"></span>
                <span
                    class="absolute inset-0 rounded-full bg-white/30 blur-md"
                    aria-hidden="true"
                ></span>
                <div class="robot-launcher pointer-events-none relative">
                    <AnimatedRobot />
                </div>
            </button>
        </div>

        <!-- Panel -->
        <div
            v-else
            class="relative flex h-[640px] max-h-[86vh] w-[440px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-white/50 bg-white/55 shadow-2xl backdrop-blur-md"
        >
            <!-- Decorative mascot backdrop, softened by the frosted layers -->
            <div
                class="pointer-events-none absolute inset-0 z-0 flex items-start justify-center overflow-hidden pt-20"
                aria-hidden="true"
            >
                <div class="robot-backdrop">
                    <AnimatedRobot />
                </div>
            </div>

            <div class="relative z-10 flex flex-1 flex-col overflow-hidden">
                <!-- Header -->
                <div
                    class="flex items-center gap-3 border-b border-white/40 bg-white/55 px-5 py-4 backdrop-blur-md"
                >
                    <div class="robot-header shrink-0">
                        <AnimatedRobot />
                    </div>
                    <div class="min-w-0 flex-1">
                        <p
                            class="truncate text-base font-semibold text-gray-800"
                        >
                            {{ t("vue.ai_widget_title") }}
                        </p>
                        <p class="truncate text-xs text-gray-500">
                            {{ t("vue.ai_widget_subtitle") }}
                        </p>
                    </div>
                    <inertia-link
                        :href="bigChatHref"
                        class="cursor-pointer rounded-full p-2 text-gray-400 transition hover:bg-white/70 hover:text-gray-700 active:scale-90"
                        :title="t('vue.ai_widget_open_full')"
                    >
                        <ArrowsPointingOutIcon class="h-5 w-5" />
                    </inertia-link>
                    <button
                        type="button"
                        class="cursor-pointer rounded-full p-2 text-gray-400 transition hover:bg-white/70 hover:text-gray-700 active:scale-90"
                        :aria-label="t('vue.ai_widget_minimize')"
                        :title="t('vue.ai_widget_minimize')"
                        @click="minimize"
                    >
                        <MinusIcon class="h-5 w-5" />
                    </button>
                    <button
                        type="button"
                        class="cursor-pointer rounded-full p-2 text-gray-400 transition hover:bg-white/70 hover:text-gray-700 active:scale-90"
                        :aria-label="t('vue.ai_widget_close')"
                        :title="t('vue.ai_widget_close')"
                        @click="closeWidget"
                    >
                        <XMarkIcon class="h-5 w-5" />
                    </button>
                </div>

                <!-- Tabs -->
                <div
                    class="flex gap-5 border-b border-white/40 bg-white/40 px-5"
                >
                    <button
                        v-for="tab in tabs"
                        :key="tab.id"
                        type="button"
                        class="-mb-px cursor-pointer border-b-2 py-3 text-sm font-medium transition"
                        :class="
                            activeTab === tab.id
                                ? 'border-gray-800 text-gray-800'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        "
                        @click="switchTab(tab.id)"
                    >
                        {{ tab.label }}
                    </button>
                </div>

                <!-- Conversation -->
                <template v-if="activeTab === 'chat'">
                    <div
                        ref="scrollContainer"
                        class="@container flex-1 space-y-4 overflow-y-auto bg-white/25 p-5 backdrop-blur-2xl"
                    >
                        <template v-if="messages.length === 0">
                            <p class="pt-8 text-center text-sm text-gray-500">
                                {{ t("vue.ai_widget_empty") }}
                            </p>
                            <AiChatCommandExamples
                                :limit="3"
                                @select="onCommandExample"
                            />
                        </template>
                        <template
                            v-for="group in messageGroups"
                            :key="group.key"
                        >
                            <AiChatToolStatusGroup
                                v-if="group.kind === 'tools'"
                                :tools="group.tools"
                            />
                            <div
                                v-else
                                :data-ai-message-id="group.message.id"
                                class="rounded-2xl p-1 transition-colors duration-500"
                                :class="
                                    highlightedMessageId === group.message.id
                                        ? 'bg-[#FF421E]/5 ring-2 ring-[#FF421E]/25'
                                        : ''
                                "
                            >
                                <AiChatMessageBubble
                                    :message="group.message"
                                    :chat-id="activeChat?.id ?? null"
                                    :read-only="readOnly"
                                    :widget="true"
                                    :latest="
                                        group.message.id === latestMessageId
                                    "
                                    @answered="refreshChat"
                                />
                            </div>
                        </template>
                        <div
                            v-if="pending || sending"
                            class="flex items-center gap-2 text-sm text-gray-400"
                        >
                            <div class="robot-thinking shrink-0">
                                <AnimatedRobot />
                            </div>
                            {{ t("vue.ai_chat_thinking") }}
                        </div>
                    </div>

                    <!-- Composer -->
                    <form
                        class="relative border-t border-white/40 bg-white/55 p-4 backdrop-blur-md"
                        :class="{
                            'ring-2 ring-inset ring-blue-300': isDragging,
                        }"
                        @submit.prevent="submit"
                        @paste="handlePaste"
                        @dragover.prevent="isDragging = true"
                        @dragenter.prevent="isDragging = true"
                        @dragleave="isDragging = false"
                        @drop.prevent="onDrop"
                    >
                        <AiSlashCommandSuggester
                            :open="slashOpen"
                            :suggestions="slashSuggestions"
                            :active-index="slashActiveIndex"
                            @select="applySlashSuggestion"
                            @hover="slashActiveIndex = $event"
                        />

                        <div
                            v-if="pageCtx.available.value"
                            class="mb-3 flex items-center gap-1.5 text-xs"
                        >
                            <button
                                type="button"
                                class="cursor-pointer rounded-full px-3 py-1.5 font-medium transition active:scale-95"
                                :class="
                                    aboutPage
                                        ? 'bg-gray-800 text-white shadow-sm'
                                        : 'bg-white/70 text-gray-600 hover:bg-white'
                                "
                                @click="aboutPage = true"
                            >
                                {{ t("vue.ai_widget_about_page") }}
                            </button>
                            <button
                                type="button"
                                class="cursor-pointer rounded-full px-3 py-1.5 font-medium transition active:scale-95"
                                :class="
                                    !aboutPage
                                        ? 'bg-gray-800 text-white shadow-sm'
                                        : 'bg-white/70 text-gray-600 hover:bg-white'
                                "
                                @click="aboutPage = false"
                            >
                                {{ t("vue.ai_widget_general") }}
                            </button>
                        </div>
                        <div v-if="selectedSkill" class="mb-2 flex">
                            <span
                                class="inline-flex max-w-full items-center gap-1.5 truncate rounded-full bg-[#FF421E]/10 px-3 py-1 text-xs font-medium text-[#FF421E]"
                            >
                                <SparklesIcon class="h-3.5 w-3.5 shrink-0" />
                                <span class="truncate">{{
                                    selectedSkill.name
                                }}</span>
                                <button
                                    type="button"
                                    class="ml-0.5 shrink-0 cursor-pointer rounded-full p-0.5 transition hover:bg-[#FF421E]/20"
                                    :aria-label="t('vue.ai_skill_detach')"
                                    @click="selectedSkill = null"
                                >
                                    <XMarkIcon class="h-3 w-3" />
                                </button>
                            </span>
                        </div>
                        <div
                            v-if="limitReached"
                            class="mb-3 rounded-lg bg-[#FF421E]/10 px-3 py-2 text-xs font-medium text-[#FF421E]"
                        >
                            {{ t("vue.ai_chat_limit_reached") }}
                        </div>
                        <TextAreaInput
                            v-model="draft"
                            :placeholder="t('vue.ai_chat_input_placeholder')"
                            :rows="2"
                            :disabled="sending || limitReached"
                            :error="uploadError || ''"
                            @keydown="onKeydown"
                        />

                        <AiChatComposerImagePreview
                            v-if="previewUrl"
                            class="mt-3"
                            :url="previewUrl"
                            :filename="image?.name"
                            :disabled="sending"
                            @remove="clearImage"
                        />

                        <div
                            class="mt-3 flex items-center justify-between gap-2"
                        >
                            <div class="flex items-center gap-2">
                                <button
                                    type="button"
                                    class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 bg-white/70 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                                    :disabled="
                                        sending ||
                                        image !== null ||
                                        isCommandDraft
                                    "
                                    :aria-label="t('vue.ai_chat_attach_image')"
                                    :title="t('vue.ai_chat_attach_image')"
                                    @click="fileInput?.click()"
                                >
                                    <PhotoIcon class="h-4 w-4" />
                                    {{ t("vue.ai_chat_attach_image") }}
                                </button>

                                <div v-if="teamSkillsActive" class="relative">
                                    <button
                                        type="button"
                                        class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 bg-white/70 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-white"
                                        :class="{
                                            'border-[#FF421E] text-[#FF421E]':
                                                selectedSkill,
                                        }"
                                        @click="toggleSkillPicker"
                                    >
                                        <SparklesIcon class="h-4 w-4" />
                                        {{ t("vue.ai_skill_attach") }}
                                    </button>

                                    <div
                                        v-if="skillPickerOpen"
                                        class="absolute bottom-full left-0 z-20 mb-1 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                                    >
                                        <div
                                            class="border-b border-gray-100 p-2"
                                        >
                                            <TextInput
                                                v-model="skillSearch"
                                                :placeholder="
                                                    t('vue.ai_skill_search')
                                                "
                                            />
                                        </div>
                                        <div
                                            class="max-h-48 overflow-y-auto p-1"
                                        >
                                            <p
                                                v-if="skillLoading"
                                                class="px-2 py-3 text-center text-xs text-gray-400"
                                            >
                                                {{ t("vue.ai_skill_loading") }}
                                            </p>
                                            <template
                                                v-else-if="
                                                    skillItems.length === 0
                                                "
                                            >
                                                <p
                                                    class="px-2 py-3 text-center text-xs text-gray-400"
                                                >
                                                    {{ t("vue.ai_skill_none") }}
                                                </p>
                                                <inertia-link
                                                    :href="
                                                        route('ai-skills.index')
                                                    "
                                                    class="block rounded-md px-2 py-1.5 text-center text-xs font-medium text-[#FF421E] transition hover:bg-[#FF421E]/5"
                                                >
                                                    {{
                                                        t("vue.ai_skill_create")
                                                    }}
                                                </inertia-link>
                                            </template>
                                            <button
                                                v-for="option in skillItems"
                                                v-else
                                                :key="option.id"
                                                type="button"
                                                class="block w-full cursor-pointer rounded-md px-2 py-1.5 text-left transition hover:bg-[#FF421E]/5"
                                                @click="pickSkill(option)"
                                            >
                                                <span
                                                    class="block truncate text-sm font-medium text-gray-800"
                                                >
                                                    {{ option.name }}
                                                </span>
                                                <span
                                                    v-if="option.summary"
                                                    class="block truncate text-xs text-gray-400"
                                                >
                                                    {{ option.summary }}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <AtomicButton
                                color="yellow"
                                size="sm"
                                type="submit"
                                :disabled="sending || limitReached || !canSend"
                            >
                                {{ t("vue.ai_chat_send") }}
                            </AtomicButton>
                        </div>

                        <p class="mt-2 text-center text-[11px] text-gray-400">
                            <span
                                v-if="usageLabel"
                                class="font-medium text-gray-500"
                                >{{ usageLabel }} ·
                            </span>
                            {{ t("vue.ai_chat_disclaimer") }}
                        </p>

                        <input
                            ref="fileInput"
                            type="file"
                            class="hidden"
                            :accept="imageAccept"
                            @change="selectFromInput"
                        />
                    </form>
                </template>

                <!-- History -->
                <div
                    v-else-if="activeTab === 'history'"
                    class="flex-1 overflow-y-auto bg-white/25 p-4 backdrop-blur-2xl"
                >
                    <AtomicButton
                        class="mb-4 w-full"
                        color="yellow"
                        size="sm"
                        @click="newChat"
                    >
                        {{ t("vue.ai_chat_new_chat") }}
                    </AtomicButton>
                    <TextInput
                        v-model="historySearch"
                        class="mb-4"
                        :placeholder="t('vue.ai_chat_search')"
                    />
                    <p
                        v-if="chatsLoading"
                        class="py-4 text-center text-sm text-gray-400"
                    >
                        {{ t("vue.loading") }}
                    </p>
                    <p
                        v-else-if="filteredChats.length === 0"
                        class="py-4 text-center text-sm text-gray-400"
                    >
                        {{ t("vue.ai_chat_no_chats") }}
                    </p>
                    <button
                        v-for="chat in filteredChats"
                        :key="chat.id"
                        type="button"
                        class="mb-1.5 block w-full cursor-pointer truncate rounded-xl px-4 py-3 text-left text-sm text-gray-700 transition-colors duration-200 hover:bg-white/40 active:scale-[0.99]"
                        @click="selectChat(chat.id)"
                    >
                        {{ chat.title }}
                    </button>
                </div>

                <!-- Saved content -->
                <div
                    v-else
                    class="@container flex-1 overflow-y-auto bg-white/25 p-4 backdrop-blur-2xl"
                >
                    <template v-if="savedDetail">
                        <button
                            type="button"
                            class="mb-3 inline-flex cursor-pointer items-center gap-1 text-xs text-gray-500 transition hover:text-gray-700"
                            @click="savedDetail = null"
                        >
                            <ArrowLeftIcon class="h-3.5 w-3.5" />
                            {{ t("vue.ai_content") }}
                        </button>
                        <h3 class="mb-2 text-base font-medium text-gray-800">
                            {{ savedDetail.title }}
                        </h3>
                        <div class="rounded-xl bg-white/70 p-4">
                            <AiChatMarkdown :content="savedDetail.content" />
                        </div>
                    </template>
                    <template v-else>
                        <TextInput
                            v-model="savedSearch"
                            class="mb-4"
                            :placeholder="t('vue.ai_content_search')"
                        />
                        <p
                            v-if="savedLoading"
                            class="py-4 text-center text-sm text-gray-400"
                        >
                            {{ t("vue.loading") }}
                        </p>
                        <p
                            v-else-if="savedItems.length === 0"
                            class="py-4 text-center text-sm text-gray-400"
                        >
                            {{ t("vue.ai_content_empty") }}
                        </p>
                        <button
                            v-for="item in savedItems"
                            :key="item.id"
                            type="button"
                            class="mb-1.5 block w-full cursor-pointer rounded-xl px-4 py-3 text-left transition-colors duration-200 hover:bg-white/40 active:scale-[0.99]"
                            @click="openSaved(item.id)"
                        >
                            <span
                                class="block truncate text-sm font-medium text-gray-800"
                            >
                                {{ item.title }}
                            </span>
                            <span
                                v-if="item.summary"
                                class="mt-0.5 line-clamp-1 block text-xs text-gray-500"
                            >
                                {{ item.summary }}
                            </span>
                        </button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import axios from "axios";
import {
    XMarkIcon,
    MinusIcon,
    ArrowsPointingOutIcon,
    ArrowLeftIcon,
    PhotoIcon,
    SparklesIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import { groupToolStatusMessages } from "@/Pages/AiChat/messageGroups";
import { useAiPageContext } from "@/composables/useAiPageContext";
import { useAiWidgetChat } from "@/composables/useAiWidgetChat";
import {
    AI_CHAT_IMAGE_ACCEPT,
    useAiChatImageUpload,
} from "@/composables/useAiChatImageUpload";
import type {
    IAiSkillAttachment,
    IAiSkillListItem,
} from "@/types/AiChat/IAiSkill";
import { useAiChatAccess } from "@/composables/useAiChatAccess";
import type { IAiSlashCommandSuggestion } from "@/types/AiChat/IAiSlashCommand";
import AiChatCommandExamples from "@/Pages/AiChat/components/AiChatCommandExamples.vue";
import AiSlashCommandSuggester from "@/Pages/AiChat/components/AiSlashCommandSuggester.vue";
import {
    isAiSlashCommandDraft,
    useAiSlashCommands,
} from "@/composables/useAiSlashCommands";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const { t } = useTranslations();
const { access } = useAiChatAccess();

// A locked trial chat is read-only, and rating it is a write too.
const readOnly = computed(() => access.value?.can_write === false);
const pageCtx = useAiPageContext();
const teamSkillsActive = computed(
    () => usePage<IRhizomPageProps>().props.inAppAiChatTeamSkillsActive,
);

// The widget only mounts for teams with access; trial teams past the message
// cap can still browse history/saved content but not send new messages.
const limitReached = computed(() => access.value?.limit_reached === true);

// Trial teams see how much of the message cap they have used, right where they type.
const usageLabel = computed<string | null>(() =>
    access.value?.level === "limited"
        ? `${access.value.used}/${access.value.limit} ${t("vue.ai_chat_usage_messages")}`
        : null,
);

const {
    chats,
    activeChat,
    messages,
    pending,
    sending,
    chatsLoading,
    savedLoading,
    savedItems,
    savedDetail,
    loadChats,
    openChat,
    refreshChat,
    backToList,
    sendMessage,
    sendCommand,
    loadSaved,
    openSaved,
    stopPolling,
} = useAiWidgetChat();

const messageGroups = computed(() => groupToolStatusMessages(messages.value));

const latestMessageId = computed<number | null>(
    () => messages.value[messages.value.length - 1]?.id ?? null,
);

type TabId = "chat" | "history" | "saved";

const open = ref(false);
const activeTab = ref<TabId>("chat");
const draft = ref("");
const aboutPage = ref(true);
const savedSearch = ref("");
const historySearch = ref("");
const scrollContainer = ref<HTMLElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const imageAccept = AI_CHAT_IMAGE_ACCEPT;

// The "/" suggester, shared verbatim with the full-page composer.
const {
    suggestions: slashSuggestions,
    open: slashOpen,
    activeIndex: slashActiveIndex,
    apply: applySlash,
    handleKeydown: handleSlashKeydown,
} = useAiSlashCommands(draft);

const {
    image,
    previewUrl,
    error: uploadError,
    selectFromInput,
    handlePaste,
    handleDrop,
    clear: clearImage,
} = useAiChatImageUpload();

const isCommandDraft = computed(() => isAiSlashCommandDraft(draft.value));

const canSend = computed(
    () => draft.value.trim() !== "" || image.value !== null,
);

const onDrop = (event: DragEvent) => {
    isDragging.value = false;
    handleDrop(event);
};

const selectedSkill = ref<IAiSkillAttachment | null>(null);
const skillPickerOpen = ref(false);
const skillItems = ref<IAiSkillListItem[]>([]);
const skillSearch = ref("");
const skillLoading = ref(false);
let skillTimer: number | undefined;

const loadSkills = async (): Promise<void> => {
    skillLoading.value = true;
    try {
        const { data } = await axios.get(route("ai-chat.api.skills"), {
            params: { q: skillSearch.value },
        });
        skillItems.value = data.items ?? [];
    } catch {
        skillItems.value = [];
    } finally {
        skillLoading.value = false;
    }
};

const toggleSkillPicker = (): void => {
    skillPickerOpen.value = !skillPickerOpen.value;
    if (skillPickerOpen.value) {
        void loadSkills();
    }
};

const pickSkill = (option: IAiSkillListItem): void => {
    selectedSkill.value = { id: option.id, name: option.name };
    skillPickerOpen.value = false;
};

watch(skillSearch, () => {
    if (skillTimer !== undefined) {
        window.clearTimeout(skillTimer);
    }
    skillTimer = window.setTimeout(() => void loadSkills(), 300);
});

const filteredChats = computed(() => {
    const term = historySearch.value.trim().toLowerCase();
    if (term === "") {
        return chats.value;
    }
    return chats.value.filter((chat) =>
        chat.title.toLowerCase().includes(term),
    );
});

// The mascot periodically greets the user (like the logout-success robot) until
// they engage with the widget, then it stops nagging.
const showGreeting = ref(false);
const greeting = computed(() =>
    pageCtx.available.value
        ? t("vue.ai_widget_greeting_page")
        : t("vue.ai_widget_greeting"),
);

let greetTimer: number | undefined;
let hideTimer: number | undefined;

const scheduleGreeting = (delay: number) => {
    greetTimer = window.setTimeout(() => {
        if (open.value === true) {
            return;
        }
        showGreeting.value = true;
        hideTimer = window.setTimeout(() => {
            showGreeting.value = false;
            scheduleGreeting(18000);
        }, 5000);
    }, delay);
};

const stopGreeting = () => {
    showGreeting.value = false;
    window.clearTimeout(greetTimer);
    window.clearTimeout(hideTimer);
};

const tabs = computed(() => [
    { id: "chat" as TabId, label: t("vue.ai_widget_tab_chat") },
    { id: "history" as TabId, label: t("vue.ai_widget_tab_history") },
    { id: "saved" as TabId, label: t("vue.ai_content") },
]);

const bigChatHref = computed(() =>
    activeChat.value
        ? route("ai-chat.show", { chat: activeChat.value.id })
        : route("ai-chat.index"),
);

// Reload both lists on every open so a persistent, long-lived widget never
// shows stale/empty data after navigation or a team switch.
const openWidget = () => {
    open.value = true;
    stopGreeting();
    void loadChats();
    void loadSaved(savedSearch.value);
};

// Minimize: collapse to the launcher but keep the last active chat and all
// in-progress state, so reopening drops the user right back where they were.
const minimize = () => {
    open.value = false;
};

// Real close: collapse and wipe the widget back to its onload state — no active
// chat, default tab, cleared draft/skill/image — so the next open starts fresh.
const closeWidget = () => {
    open.value = false;
    backToList();
    focusMessageId.value = null;
    activeTab.value = "chat";
    aboutPage.value = true;
    draft.value = "";
    selectedSkill.value = null;
    skillPickerOpen.value = false;
    savedDetail.value = null;
    savedSearch.value = "";
    historySearch.value = "";
    clearImage();
};

const switchTab = (id: TabId) => {
    activeTab.value = id;
    if (id === "saved") {
        void loadSaved(savedSearch.value);
    }
    if (id === "history") {
        void loadChats();
    }
};

const selectChat = async (id: number) => {
    await openChat(id);
    activeTab.value = "chat";
};

const newChat = () => {
    backToList();
    focusMessageId.value = null;
    activeTab.value = "chat";
};

const submit = () => {
    const value = draft.value.trim();
    if (sending.value || (value === "" && image.value === null)) {
        return;
    }

    if (limitReached.value) {
        return;
    }

    // A typed command runs its tool within the request: no attachment, no page
    // context, no reply to wait for. It still spends a message like any other.
    // The composer is cleared only once it ran — a refused command comes back
    // with the reason, and the sender needs the line to correct.
    if (isAiSlashCommandDraft(value)) {
        void sendCommand(value).then((sent: boolean) => {
            if (sent) {
                draft.value = "";
                clearImage();
            }
        });

        return;
    }

    const pageContext = aboutPage.value ? pageCtx.build() : null;
    focusMessageId.value = null;
    const attached = image.value;
    const skill = selectedSkill.value;
    draft.value = "";
    clearImage();
    selectedSkill.value = null;
    void sendMessage(value, pageContext, attached, skill);
};

const onKeydown = (event: KeyboardEvent) => {
    // The popover gets first refusal on the keys it owns.
    if (slashOpen.value && handleSlashKeydown(event)) {
        event.preventDefault();
        return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        submit();
    }
};

const applySlashSuggestion = (suggestion: IAiSlashCommandSuggestion): void => {
    applySlash(suggestion);
};

const onCommandExample = (command: string): void => {
    draft.value = command;
};

// Arriving from a navigation button, the conversation opens on the message that
// offered the jump — not at the bottom, where the user would have to scroll back
// to find what sent them here. The scroll is computed inside the container so the
// destination page itself never moves, and the bubble is marked briefly so the
// eye lands on it.
const HIGHLIGHT_MS = 2600;
const focusMessageId = ref<number | null>(null);
const highlightedMessageId = ref<number | null>(null);
let highlightTimer: number | undefined;

const focusRequestedMessage = (): boolean => {
    const container = scrollContainer.value;
    const messageId = focusMessageId.value;
    if (container === null || messageId === null) {
        return false;
    }

    // The request outlives a miss: clearing a pending chat load, an optimistic
    // bubble or a tool-status row can move the conversation before the target
    // message is in the DOM, and consuming the anchor on that first pass would
    // drop the user at the bottom anyway. It is released once it lands, and when
    // the user sends or switches — never on a merely early attempt.
    const target = container.querySelector<HTMLElement>(
        `[data-ai-message-id="${messageId}"]`,
    );
    if (target === null) {
        return false;
    }
    focusMessageId.value = null;

    const offset =
        target.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
    container.scrollTop +=
        offset - (container.clientHeight - target.clientHeight) / 2;

    highlightedMessageId.value = messageId;
    window.clearTimeout(highlightTimer);
    highlightTimer = window.setTimeout(() => {
        highlightedMessageId.value = null;
    }, HIGHLIGHT_MS);

    return true;
};

const scrollToBottom = () => {
    nextTick(() => {
        if (focusRequestedMessage() === true) {
            return;
        }
        if (scrollContainer.value) {
            scrollContainer.value.scrollTop =
                scrollContainer.value.scrollHeight;
        }
    });
};

watch(() => [messages.value.length, pending.value], scrollToBottom);

let savedTimer: number | undefined;
watch(savedSearch, (value) => {
    if (savedTimer !== undefined) {
        window.clearTimeout(savedTimer);
    }
    savedTimer = window.setTimeout(() => void loadSaved(value), 300);
});

// Seamless return: a "back to page" chip or a navigation button lands the user
// here with ?ai_widget_chat=<id> and ?ai_widget_message=<id>. Open the widget on
// that page with the conversation active and anchored to the message they left
// from, then clean the URL so a refresh or share link stays normal.
const openFromReturnParam = (): void => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("ai_widget_chat");
    if (raw === null) {
        return;
    }
    const rawMessage = params.get("ai_widget_message");

    params.delete("ai_widget_chat");
    params.delete("ai_widget_message");
    const query = params.toString();
    const cleanUrl =
        window.location.pathname +
        (query === "" ? "" : `?${query}`) +
        window.location.hash;
    window.history.replaceState({}, "", cleanUrl);

    const chatId = Number(raw);
    if (!Number.isInteger(chatId) || chatId <= 0) {
        return;
    }

    const messageId = Number(rawMessage);
    focusMessageId.value =
        Number.isInteger(messageId) && messageId > 0 ? messageId : null;

    openWidget();
    void selectChat(chatId);
};

let stopNavigateListener: (() => void) | undefined;

onMounted(() => {
    scheduleGreeting(1500);
    openFromReturnParam();
    // The widget lives in the persistent layout, so it does not remount on an
    // Inertia visit — react to navigation to catch the return param on arrival.
    stopNavigateListener = router.on("navigate", () => openFromReturnParam());
});

onUnmounted(() => {
    stopPolling();
    stopGreeting();
    window.clearTimeout(highlightTimer);
    stopNavigateListener?.();
});
</script>

<style scoped>
/* Launcher mascot: larger than the circle and pushed up so it peeks out the top.
   A gentle idle bob makes it feel alive and inviting to click. */
.robot-launcher {
    width: 78px;
    height: 78px;
    margin-top: -18px;
    animation: launcherOffer 3s ease-in-out infinite;
    will-change: transform;
}

@keyframes launcherOffer {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    30% {
        transform: translateY(-9px) rotate(-4deg);
    }
    60% {
        transform: translateY(-4px) rotate(3deg);
    }
}

/* Greeting speech bubble pop, mirroring the mascot's own message animation. */
.greet-enter-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.greet-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}
.greet-enter-from,
.greet-leave-to {
    opacity: 0;
    transform: translateY(6px) scale(0.9);
}

/* Attention ripple radiating from the launcher to draw the user's eye. */
.launcher-pulse {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    box-shadow: 0 0 0 0 rgba(255, 66, 30, 0.35);
    animation: launcherPulse 2.6s ease-out infinite;
}

@keyframes launcherPulse {
    0% {
        box-shadow: 0 0 0 0 rgba(255, 66, 30, 0.3);
    }
    70% {
        box-shadow: 0 0 0 16px rgba(255, 66, 30, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(255, 66, 30, 0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .robot-launcher,
    .launcher-pulse {
        animation: none !important;
    }
}
.robot-header {
    width: 42px;
    height: 42px;
}
.robot-thinking {
    width: 34px;
    height: 34px;
}
/* Decorative, blurred mascot behind the panel — the DMARCeye glassy identity. */
.robot-backdrop {
    width: 300px;
    height: 300px;
    opacity: 0.16;
    filter: grayscale(20%) blur(6px);
}

.robot-launcher :deep(.robot-anim),
.robot-launcher :deep(.robot-anim svg) {
    width: 78px !important;
    height: 78px !important;
}
.robot-header :deep(.robot-anim),
.robot-header :deep(.robot-anim svg) {
    width: 42px !important;
    height: 42px !important;
}
.robot-thinking :deep(.robot-anim),
.robot-thinking :deep(.robot-anim svg) {
    width: 34px !important;
    height: 34px !important;
}
.robot-backdrop :deep(.robot-anim),
.robot-backdrop :deep(.robot-anim svg) {
    width: 300px !important;
    height: 300px !important;
}

.robot-launcher :deep(#robotMessage),
.robot-header :deep(#robotMessage),
.robot-thinking :deep(#robotMessage),
.robot-backdrop :deep(#robotMessage) {
    display: none !important;
}
</style>
